import { ColDef, ColGroupDef, ValueGetterParams } from 'ag-grid-community';
import { flatMap, uniq } from 'lodash';
import { dataFormatting } from '../../_lib/lib';
import { IPooffersItems } from '../../types/IPoOffers';
import { formatImportDate } from '../PreOrders/Utils';

export const narrowGroupsByShipWindows = (
  groupedItems: any,
  matchesSelectedShipWindows: (item: IPooffersItems) => boolean,
  narrowedTab?: string
) => {
  const narrowedGroups: any = {};
  const badgeCount: any = {};

  Object.keys(groupedItems).forEach((tab: string) => {
    const warehouses = groupedItems[tab] || {};
    const narrowedWarehouses: any = {};
    let tabBadgeCount = 0;
    // the ship windows come from one tab, the others are counted whole
    const keepItem = !narrowedTab || tab === narrowedTab ? matchesSelectedShipWindows : () => true;

    Object.keys(warehouses).forEach((warehouseCode: string) => {
      const items = (warehouses[warehouseCode]?.items || []).filter(keepItem);
      if (items.length === 0) return;
      narrowedWarehouses[warehouseCode] = {
        items,
        subTotal: items.reduce(
          (subTotal: number, item: IPooffersItems) =>
            subTotal + item.offerPrice * item.offerQuantity,
          0
        ),
      };
      tabBadgeCount += items.length;
    });

    narrowedGroups[tab] = narrowedWarehouses;
    badgeCount[tab] = tabBadgeCount;
  });

  return { groupedItems: narrowedGroups, badgeCount };
};

export const getItemsOfWarehouses = (warehouses: any): IPooffersItems[] =>
  flatMap(Object.values(warehouses || {}), (warehouse: any) => warehouse?.items || []);

// empty while the filter is off, the summary is then asked for every offer
export const getSelectedItemElectIds = (
  items: IPooffersItems[],
  isShipDateFilterOn: boolean,
  matchesSelectedShipWindows: (item: IPooffersItems) => boolean
): string => {
  if (!isShipDateFilterOn) return '';
  const itemElectIds = items
    .filter(matchesSelectedShipWindows)
    .map((item: IPooffersItems) => item.itemElectId)
    .filter(Boolean);
  return uniq(itemElectIds)
    .sort((left: number, right: number) => left - right)
    .join(',');
};

export const getPoOfferImportColumnDefinitions = (
  itemGroupGetter: (params: ValueGetterParams) => any
): Array<ColDef | ColGroupDef> => [
  {
    headerName: 'Row',
    headerClass: 'px-import-review-header',
    children: [
      {
        valueGetter: itemGroupGetter,
        rowGroup: true,
        hide: true,
      },
      {
        headerName: '',
        field: 'rowNumber',
        minWidth: 50,
        maxWidth: 80,
      },
    ],
  },
  {
    headerName: 'Draft Items',
    headerClass: 'px-import-review-header',
    children: [
      {
        headerName: 'Item#',
        colId: 'itemNumber',
        field: 'itemNumber',
        valueGetter: (params: ValueGetterParams) => {
          return params?.data?.itemNumber ?? '--';
        },
        minWidth: 100,
        maxWidth: 200,
      },
      {
        headerName: 'Offer Item#',
        colId: 'itemElectId',
        field: 'itemElectId',
        valueGetter: (params: ValueGetterParams) => {
          return params?.data?.itemElectId ?? '--';
        },
        minWidth: 100,
        maxWidth: 200,
      },
      {
        headerName: 'Warehouse',
        field: 'warehouse',
        colId: 'warehouse',
        cellClass: 'po-import-longtext',
        wrapText: true,
        valueGetter: (params: ValueGetterParams) => {
          return params?.data?.warehouse ?? '--';
        },
        minWidth: 100,
        maxWidth: 200,
      },
      {
        headerName: 'Shipping Start Date',
        field: 'shippingStartDate',
        colId: 'shippingStartDate',
        wrapText: true,
        valueGetter: (params: ValueGetterParams) => {
          return formatImportDate(params?.data?.['Ship Start Date']);
        },
        minWidth: 125,
        maxWidth: 200,
      },
      {
        headerName: 'Shipping End Date',
        field: 'shippingEndDate',
        colId: 'shippingEndDate',
        wrapText: true,
        valueGetter: (params: ValueGetterParams) => {
          return formatImportDate(params?.data?.['Ship End Date']);
        },
        minWidth: 125,
        maxWidth: 200,
      },
      {
        headerName: 'Quantity Forecast',
        field: 'quantityForecast',
        colId: 'quantityForecast',
        wrapText: true,
        valueGetter: (params: ValueGetterParams) => {
          return params?.data?.['Forecast Quantity'] ?? '--';
        },
        minWidth: 100,
        maxWidth: 200,
      },
      {
        headerName: 'New Offer Quantity',
        colId: 'offerQuantity',
        field: 'offerQuantity',
        wrapText: true,
        valueGetter: (params: ValueGetterParams) => {
          return dataFormatting('integer', params?.data?.offerQuantity);
        },
        minWidth: 100,
        maxWidth: 200,
      },
      {
        headerName: 'New Offer Price',
        colId: 'offerPrice',
        field: 'offerPrice',
        wrapText: true,
        valueGetter: (params: ValueGetterParams) => {
          return dataFormatting('currency', params?.data?.offerPrice);
        },
        minWidth: 100,
        maxWidth: 200,
      },
      {
        headerName: 'New Take All',
        colId: 'takeAll',
        field: 'takeAll',
        wrapText: true,
        valueGetter: (params: ValueGetterParams) => {
          return params?.data?.takeAll ?? '--';
        },
        minWidth: 100,
        maxWidth: 200,
      },
    ],
  },
];
